import React from "react";
import { BCMSRecord, useBCMSContext, useParams, useStateManager, BCMSStateManagerCRUD } from "bcms-react";
import Classes, { Notification, NotificationDetail, ScheduledAlert, ScheduledTime, Weekday } from "model";

import { CenterContentContainer, PageContainer, PageContentView } from "components/shared";
import UINotifiHeader from "components/shared/notifi/UINotifiHeader";
import AlertDetailsAccountWidget from "./components/AlertDetailsAccountWidget";

import "./mobile.css";

/**
 * @name AlertDetailsPage
 * @param {OBJECT} data - Data passed by either a workflow or capture
 * @param {OBJECT} ui - UI passed by either a workflow or capture
 * @returns {React.Component}
 * @description The main component for the alert details page
 */
export default function AlertDetailsPage(props) {
    const globalController = useBCMSContext();
    BCMSRecord.globalControllerFor(Classes, globalController);

    const alertID = useParams().alertID ?? "1";

    const data = { ...getDefaultPageData(alertID), ...props.data ?? {} };
    const ui = { ...getDefaultPageUI(), ...props.ui ?? {} };

    const getByID = (array, id) => array.find(item => item.id === id);
    const insertIntoArray = (array, value) => array.map(item => item.id === value.id ? value : item, []);

    const controller = (state, setState) => ({
        data: state.data,
        ui: state.ui,

        onUpdate: (id, key) => evt => {
            const detail = getByID(state.data.notificationDetails, id);

            NotificationDetail.update(detail)
                .submit(Classes);

            if(detail.scheduledAlert) {
                ScheduledAlert.update(detail.scheduledAlert).submit(Classes);
                ScheduledTime.update(detail.scheduledAlert.scheduledTime).submit(Classes);
            }

            BCMSStateManagerCRUD.init(state)
                .update([ "ui", key, "action" ], "updated")
                .submit(setState);
        },

        onAddDelete: (id, key, action, value) => {
            const notifications = state.data.notificationDetails;
            const detail = getByID(notifications, id);

            detail.isEnabled = value;
            detail.amount = !value ? 0 : detail.amount;

            const updatedArray = insertIntoArray(notifications, detail);
            const updates = [];
            updates.push(
                NotificationDetail.update({ ...detail.record })
            );

            if(action === "deleted") {
                if(detail.scheduledAlert) {
                    updates.push(
                        ScheduledAlert.update({ 
                            ...detail.scheduledAlert.record,
                            isEnabled: false,
                            weekdayIDs: []
                         })
                    );
                    updates.push(
                        ScheduledTime.update({ 
                            ...detail.scheduledAlert.scheduledTime.record,
                            hour: "",
                            minute: "",
                            timeOfDay: "AM",
                            type: ""
                        })
                    );
                }
            } else if(detail.scheduledAlert) {
                updates.push(
                    ScheduledAlert.update({ 
                        ...detail.scheduledAlert.record
                     })
                );
                updates.push(
                    ScheduledTime.update({ 
                        ...detail.scheduledAlert.scheduledTime.record
                    })
                );
            }

            BCMSStateManagerCRUD.init(state)
                .update([ "data", "notificationDetails" ], updatedArray)
                .update([ "ui", key, "action" ], action)
                .submit(setState);

            BCMSRecord.batch([
                ...updates
                ], globalController, Classes);
        },

        updateAmount: id => value => {
            const notifications = state.data.notificationDetails;
            const detail = getByID(notifications, id);
            detail.amount = value;
            const updatedArray = insertIntoArray(notifications, detail);
            
            BCMSStateManagerCRUD.init(state)
                .update([ "data", "notificationDetails" ], updatedArray)
                .submit(setState);
        },

        setTimeOfDayIsValid: detail => {
            const scheduledAlert = detail.scheduledAlert;
    
            if(scheduledAlert) {
                if(scheduledAlert.scheduledTime) {
                    const { weekdayIDs } = scheduledAlert;
                    const { hour, minute } = scheduledAlert.scheduledTime;

                    if(weekdayIDs.length === 0) return false;
                    // temp: this gets around an init bug in the class.
                    if(weekdayIDs.length === 1 && weekdayIDs[0] === "") return false;
                    if((hour === "" || isNaN(hour)) ||
                    (minute === "" || isNaN(minute))) {
                        return false
                    } else {
                        return true;
                    }
                }
            }
            return false;
        },

        updateTimeOfDay: (detailID, scheduledTimeProp) => value => {
            const notifications = state.data.notificationDetails;
            const detail = getByID(notifications, detailID);
            const currScheduledTime = ScheduledTime.init({ ...detail.scheduledAlert.scheduledTime.record });
            detail.scheduledAlert = ScheduledAlert.init({ ...detail.scheduledAlert.record });
            detail.scheduledAlert.scheduledTime = ScheduledTime.init({ ...currScheduledTime.record });
            detail.scheduledAlert.scheduledTime[scheduledTimeProp] = value;
            const updatedArray = insertIntoArray(notifications, detail);
            
            BCMSStateManagerCRUD.init(state)
                .update([ "data", "notificationDetails" ], updatedArray)
                .submit(setState);
        },

        updateScheduledTimeWeekdays: (detailID, weekdayIDsProp) => (weekdayID, weekdayChecked) => {
            const notifications = state.data.notificationDetails;
            const detail = getByID(notifications, detailID);

            // Update the selected day of the week enabled status.
            if(weekdayIDsProp === "weekdayIDs") {
                let selectedWeekdayIDs = detail.scheduledAlert.weekdayIDs;
                if(weekdayChecked) {
                    selectedWeekdayIDs = selectedWeekdayIDs.filter( id => id !== weekdayID);
                } else {
                    selectedWeekdayIDs = [ ...selectedWeekdayIDs, weekdayID ].sort((a, b) => a - b);
                }
    
                const currScheduledTime = ScheduledTime.init({ ...detail.scheduledAlert.scheduledTime.record });
                detail.scheduledAlert = ScheduledAlert.init({ 
                    ...detail.scheduledAlert.record, 
                    isEnabled: true, // enable alert once hours, minutes, and time of day are all set
                    weekdayIDs: [ ...selectedWeekdayIDs ]
                });

                detail.scheduledAlert.scheduledTime = ScheduledTime.init({ ...currScheduledTime.record });
            } else {
                // update hours, minutes, time of day.
            }

            const updatedArray = insertIntoArray(notifications, detail);
            
            BCMSStateManagerCRUD.init(state)
                .update([ "data", "notificationDetails" ], updatedArray)
                .submit(setState);
        },

        updateContactIds: id => contactId => {
            const notifications = state.data.notificationDetails;
            const detail = getByID(notifications, id);

            const ids = detail.contactInfoEnabledIDs;

            if(ids.includes(contactId)) { ids.splice(ids.indexOf(contactId), 1); }
            else { ids.push(contactId); }

            detail.contactInfoEnabledIDs = ids;

            const updatedArray = insertIntoArray(notifications, detail);

            BCMSStateManagerCRUD.init(state)
                .update([ "data", "notificationDetails" ], updatedArray)
                .submit(setState);
        }
    });

    return (
        <PageContainer className="alert-details-page" state={ { data, ui } } controller={ controller }>
            <AlertDetailsContent />
        </PageContainer>
    )
}

export const getDefaultPageData = alert => {
    const notification = Notification.initByID(alert);
    const notificationDetails = NotificationDetail.getByNotificationID(notification.id);

    return { notification, notificationDetails }
};
export const getDefaultPageUI = () => ({});

function AlertDetailsContent(props) {
    const { data } = useStateManager();
    const detailItems = data.notificationDetails.map(detail => {
        return <AlertDetailsAccountWidget key={ `Detail_${ detail.id }` } alertDetail={ detail } />
    }, []);

    return (
        <PageContentView>
            <div className="notifications-page-container">
                <CenterContentContainer>
                    <UINotifiHeader headerText={ data.notification.name } dataPoint="alert_details_header" />

                    { detailItems }
                </CenterContentContainer>
            </div>
        </PageContentView>
    )
}